import { useEffect, useMemo } from 'react';
import { PrimeMode, PrimeQuotaResult } from '@stores/prime-quota';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { readCookie } from '@utils/cookies';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';

interface UsePrimePracticeQuotaReturn {
  isQuotaExhausted: boolean;
  unlockedPracticeUids: string[];
  primePracticeQuota: PrimeQuotaResult;
}

const usePrimePracticeQuota = (
  goalUID: string | undefined
): UsePrimePracticeQuotaReturn => {
  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const fetchUnlockedVideosUids = usePrimeQuotaStore(
    (state) => state.fetchUnlockedVideosUids
  );
  const primePracticeQuota = usePrimeQuotaStore((state) =>
    state.getQuotaForMode(PrimeMode.PRACTICE)
  );
  const unlockedPracticeUids = usePrimeQuotaStore(
    (state) => state.unlockedVideosUids
  );

  const isSubscribed = usePlusIconicSubscribed({ goalUID });
  const accessToken = readCookie('accessToken');
  const isLoggedIn = Boolean(accessToken);

  useEffect(() => {
    if (goalUID && isLoggedIn) {
      fetchQuotaDetails({ goalUID, accessToken });
      fetchUnlockedVideosUids({ goalUID, accessToken });
    }
  }, [
    goalUID,
    accessToken,
    isLoggedIn,
    fetchQuotaDetails,
    fetchUnlockedVideosUids
  ]);

  const isQuotaExhausted = useMemo(() => {
    if (!isLoggedIn) return false;
    if (isSubscribed) return false;
    return primePracticeQuota.isQuotaExhausted;
  }, [primePracticeQuota, isSubscribed, isLoggedIn]);

  return { isQuotaExhausted, unlockedPracticeUids, primePracticeQuota };
};

export default usePrimePracticeQuota;
